import { Source, SourceChunk } from '../types/chat';

/** Matches a complete inline citation token `[[ref:n]]` (n = 1-based integer). */
export const CITATION_TOKEN_REGEX = /\[\[ref:(\d+)\]\]/g;

const toNumber = (value: unknown): number | undefined => {
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

const normalizeChunk = (raw: any): SourceChunk => ({
  ...raw,
  cited: raw?.cited === true ? true : raw?.cited,
  pageNumber: toNumber(raw?.page_number ?? raw?.pageNumber),
  chunkNumber: toNumber(raw?.chunk_number ?? raw?.chunkNumber),
  fileName: raw?.file_name ?? raw?.fileName,
});

/**
 * Maps a wire/persisted source (snake_case from the stream or Metadata.sources)
 * into the client {@link Source} shape, preserving cited chunks + citation index.
 */
export const normalizeSourceFromWire = (raw: any): Source => {
  const fileId = raw?.file_id ?? raw?.fileId ?? '';
  const chunks: SourceChunk[] = Array.isArray(raw?.chunks) ? raw.chunks.map(normalizeChunk) : [];
  let fileName: string = raw?.file_name ?? raw?.fileName ?? '';
  if (!fileName && chunks[0]?.fileName) {
    fileName = chunks[0].fileName as string;
  }
  const citationIndex = toNumber(raw?.citation_index ?? raw?.citationIndex);
  return {
    fileId,
    fileName: fileName || `file_${fileId}`,
    chunks,
    uri: raw?.uri || undefined,
    ...(citationIndex != null ? { citationIndex } : {}),
  };
};

/** Deep-link page for a pill: first `cited` chunk's page, else the first chunk's page. */
export const getCitedPage = (source?: Source): number | undefined => {
  const chunks = source?.chunks ?? [];
  const cited = chunks.find((chunk) => chunk?.cited === true);
  const page = cited?.pageNumber ?? chunks[0]?.pageNumber;
  return typeof page === 'number' ? page : undefined;
};

/**
 * Resolves an inline `[[ref:n]]` to its pill via `citationIndex`. Falls back to a
 * 1-based positional match only when the payload predates citation indices.
 */
export const resolvePillByCitationIndex = (
  sources: Source[] | undefined,
  index: number,
): Source | undefined => {
  if (!Array.isArray(sources) || sources.length === 0) return undefined;
  const anyIndexed = sources.some((source) => typeof source.citationIndex === 'number');
  if (anyIndexed) {
    return sources.find((source) => source.citationIndex === index);
  }
  return sources[index - 1];
};

/** Stable DOM id for a pill so a citation marker can scroll it into view. */
export const pillDomId = (
  generationId: string,
  source: Pick<Source, 'citationIndex' | 'fileId'>,
): string => `source-pill-${generationId}-${source.citationIndex ?? source.fileId}`;

const HIGHLIGHT_CLASSES = ['ring-2', 'ring-primary', 'ring-offset-1'];

/** Scrolls the pill with the given id into view and briefly rings it. */
export const highlightSourcePill = (domId: string): void => {
  if (typeof document === 'undefined') return;
  const el = document.getElementById(domId);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  el.classList.add(...HIGHLIGHT_CLASSES);
  window.setTimeout(() => el.classList.remove(...HIGHLIGHT_CLASSES), 1600);
};
